import React, { useContext, useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faChevronDown, faCheck, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import UserTagsContext from '../context/UserTagsContext';
import { TagVocabItem } from '../api/UserTagsAPI';

// A tag field for a move (scope="Move", identified by `pgn`) or a whole game (scope="Game"). Each
// vocabulary category gets its own typeahead dropdown; selected tags show as removable chips. The
// vocabulary is the server's list. Signed-out visitors get the login overlay instead of a write
// (UserTagsProvider).

// Preferred order of the category dropdowns; unlisted categories follow alphabetically.
const CATEGORY_ORDER = [
  'Pattern',
  'Calculation',
  'Decision',
  'State of mind',
  'How it went',
  'Went well',
];

function categoryRank(cat: string): number {
  const i = CATEGORY_ORDER.indexOf(cat);
  return i === -1 ? CATEGORY_ORDER.length : i;
}

// One category's dropdown: a button opening a portalled, searchable menu.
function CategoryDropdown(props: {
  category: string;
  items: TagVocabItem[];
  selected: string[];
  onToggle: (slug: string) => void;
}): React.ReactNode {
  const { category, items, selected, onToggle } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // The menu is position:fixed to escape the move panel's clipping scroll container. It's pinned to the
  // button by whichever edge has room (top when opening down, bottom when flipping up); maxHeight caps
  // it to the available space.
  const [menuPos, setMenuPos] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    maxHeight: number;
  } | null>(null);

  const selectedInCat = items.filter((i) => selected.includes(i.Slug)).length;

  // Alphabetical, filtered by the typeahead query (label or slug).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((i) => q === '' || i.Label.toLowerCase().includes(q) || i.Slug.includes(q))
      .sort((a, b) => a.Label.localeCompare(b.Label));
  }, [items, query]);

  // Open in the direction with more room and cap the height to it, one edge pinned to the button.
  const placeMenu = () => {
    const btn = btnRef.current;
    if (btn == null) {
      return;
    }
    const r = btn.getBoundingClientRect();
    const menuW = Math.min(320, window.innerWidth * 0.82);
    const gap = 4;
    const margin = 8;
    const cap = 340;
    let left = r.left;
    if (left + menuW > window.innerWidth - margin) {
      left = window.innerWidth - menuW - margin;
    }
    left = Math.max(margin, left);
    const spaceBelow = window.innerHeight - r.bottom - gap - margin;
    const spaceAbove = r.top - gap - margin;
    // Prefer below unless it's cramped and above genuinely has more room.
    if (spaceBelow >= Math.min(cap, 200) || spaceBelow >= spaceAbove) {
      setMenuPos({ left, top: r.bottom + gap, maxHeight: Math.min(cap, spaceBelow) });
    } else {
      setMenuPos({ left, bottom: window.innerHeight - r.top + gap, maxHeight: Math.min(cap, spaceAbove) });
    }
  };

  // Position and focus the search once the menu is open (before paint).
  useLayoutEffect(() => {
    if (open) {
      placeMenu();
    } else {
      setMenuPos(null);
      setQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      searchRef.current?.focus();
    }
  }, [open, menuPos]);

  // Outside click / Escape closes; keep the fixed menu pinned while the panel scrolls.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(t) &&
        btnRef.current &&
        !btnRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    const onScroll = (e: Event) => {
      if (menuRef.current && menuRef.current.contains(e.target as Node)) {
        return;
      }
      placeMenu();
    };
    const onResize = () => placeMenu();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="sc-tagdd">
      <button
        type="button"
        ref={btnRef}
        className={`sc-tagdd-btn${selectedInCat > 0 ? ' has-selected' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {category}
        {selectedInCat > 0 ? <span className="sc-tagdd-count">{selectedInCat}</span> : null}
        <FontAwesomeIcon icon={faChevronDown} className="sc-tagdd-caret" aria-hidden="true" />
      </button>
      {open && menuPos != null
        ? createPortal(
            <div
              className="sc-tag-menu sc-tagdd-menu"
              ref={menuRef}
              style={{
                left: menuPos.left,
                top: menuPos.top,
                bottom: menuPos.bottom,
                maxHeight: menuPos.maxHeight,
              }}
            >
              <div className="sc-tagdd-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${category.toLowerCase()}…`}
                  aria-label={`Search ${category}`}
                />
              </div>
              <div className="sc-tagdd-list">
                {filtered.length === 0 ? (
                  <div className="sc-tag-menu-empty">No matches.</div>
                ) : (
                  filtered.map((o) => {
                    const on = selected.includes(o.Slug);
                    return (
                      <button
                        key={o.Slug}
                        type="button"
                        className={`sc-tag-menu-item${on ? ' selected' : ''}`}
                        onClick={() => {
                          onToggle(o.Slug);
                          setOpen(false);
                        }}
                        title={o.Hint}
                      >
                        <span className="sc-tag-menu-item-main">
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="sc-tag-menu-check"
                            style={{ visibility: on ? 'visible' : 'hidden' }}
                            aria-hidden="true"
                          />
                          <span className="sc-tag-menu-item-label">{o.Label}</span>
                        </span>
                        {o.Hint ? (
                          <span className="sc-tag-menu-item-hint">{o.Hint}</span>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>,
            // Portal into the enclosing modal when there is one: react-bootstrap's modal traps focus, so a menu
            // on <body> couldn't take typing. <body> otherwise.
            btnRef.current?.closest('.modal') ?? document.body,
          )
        : null}
    </div>
  );
}

export default function TagPicker(props: {
  scope: 'Move' | 'Game';
  pgn?: string | null;
  label: string;
}): React.ReactNode {
  const { scope, pgn, label } = props;
  const { vocabulary, moveTags, gameTags, toggleMoveTag, toggleGameTag } =
    useContext(UserTagsContext);

  const options = useMemo(() => vocabulary.filter((v) => v.Scope === scope), [vocabulary, scope]);

  const selected = scope === 'Move' ? (pgn != null ? moveTags[pgn] ?? [] : []) : gameTags;

  const toggle = (theme: string) => {
    if (scope === 'Move') {
      if (pgn != null) {
        toggleMoveTag(pgn, theme);
      }
    } else {
      toggleGameTag(theme);
    }
  };

  // The category dropdowns, in preferred order.
  const categories = useMemo(() => {
    const byCat = new Map<string, TagVocabItem[]>();
    for (const o of options) {
      const arr = byCat.get(o.Category) ?? [];
      arr.push(o);
      byCat.set(o.Category, arr);
    }
    return Array.from(byCat.entries()).sort(
      ([a], [b]) => categoryRank(a) - categoryRank(b) || a.localeCompare(b),
    );
  }, [options]);

  const labelFor = (slug: string) => options.find((o) => o.Slug === slug)?.Label ?? slug;

  if (options.length === 0) {
    return null;
  }
  // A move picker needs a move to attach to.
  if (scope === 'Move' && pgn == null) {
    return null;
  }

  return (
    <div className={`sc-tagpicker sc-tagpicker-${scope.toLowerCase()}`}>
      <div className="sc-tagpicker-label">
        <FontAwesomeIcon icon={faTag} className="sc-tagpicker-labelico" aria-hidden="true" />
        {label}
      </div>
      {selected.length > 0 ? (
        <div className="sc-tagpicker-chips">
          {selected.map((slug) => (
            <button
              key={slug}
              type="button"
              className="sc-tag sc-tag-on"
              onClick={() => toggle(slug)}
              title="Remove this tag"
            >
              {labelFor(slug)}
              <span className="sc-tag-x" aria-hidden="true">
                ×
              </span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="sc-tagpicker-dds">
        {categories.map(([cat, items]) => (
          <CategoryDropdown
            key={cat}
            category={cat}
            items={items}
            selected={selected}
            onToggle={toggle}
          />
        ))}
      </div>
    </div>
  );
}
